import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import Hero from "../../components/Hero/Hero";
import SearchBar from "../../components/SearchBar/SearchBar";
import GMBScoring from "../../components/GMBScoring/GMBScoring";
import ReCAPTCHA from "react-google-recaptcha";
import toast from "../../components/ui/toast";
import { buildAuditCriteria } from "../../components/GMBScoring/criteria";
import Seo from "../../i18n/Seo";
import useLang from "../../i18n/useLang";
import { demoUrl } from "../../i18n/links";
import content from "./content";
import "./audit.css";
import InfoStats from "./InfoStats";
import ModalMoreInfo from "./ModalMoreInfo";
import PageFooter from "../../components/PageFooter/PageFooter";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4000/tech";

// Delay before each loading message (texts in content.loadingSteps).
const LOADING_DELAYS = [100, 5000, 10000, 16000, 25000];

const Home = () => {
  const lang = useLang();
  const t = content[lang];
  const [GMBInfo, setGMBInfo] = useState();
  const [displayGMBSection, setDisplayGMBSection] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [loadingStep, setLoadingStep] = useState(0);
  const [hasFullAccess, setFullAccess] = useState(false);
  const [auditError, setAuditError] = useState(null);
  const recaptchaRef = useRef();
  const [businessAddress, setBusinessAddress] = useState("");
  const [modalBusinessInfo, setModalBusinessInfo] = useState(false);
  const [isSA, setIsSA] = useState(false);
  const loadingTimers = useRef([]);
  let location = useLocation();

  useEffect(() => {
    if (location.search.includes("access=localranker")) {
      setIsSA(true);
    }
  }, []);

  useEffect(() => {
    return () => {
      loadingTimers.current.forEach(clearTimeout);
    };
  }, []);

  const handleOpenModalBusinessInfo = () => {
    if (isLoading) return;
    setModalBusinessInfo(true);
  };

  const resetState = () => {
    setIsLoading(false);
    setLoadingMessage("");
    setLoadingStep(0);
    loadingTimers.current.forEach(clearTimeout);
    loadingTimers.current = [];
  };

  const submitBusiness = async (businessAddress, businessInfo) => {
    if (isLoading) return;

    try {
      setModalBusinessInfo(false);
      setAuditError(null);
      setDisplayGMBSection(false);
      setGMBInfo(undefined);

      const tokenCaptcha = isSA ? true : await recaptchaRef.current.executeAsync();
      if (!tokenCaptcha) {
        toast.warning(t.captcha);
        return;
      }

      setIsLoading(true);

      loadingTimers.current.forEach(clearTimeout);
      loadingTimers.current = [];
      LOADING_DELAYS.forEach((delay, i) => {
        const timer = setTimeout(() => {
          setLoadingMessage(t.loadingSteps[i]);
          setLoadingStep(i);
        }, delay);
        loadingTimers.current.push(timer);
      });

      const response = await fetch(
        `${API_URL}/gmb-audit?businessAddress=${encodeURIComponent(businessAddress)}&lang=${lang}${
          businessInfo
            ? `&businessInfo=${encodeURIComponent(JSON.stringify(businessInfo))}`
            : isSA
              ? "&sa=true"
              : ""
        }`,
        { method: "POST" },
      );

      if (response.status === 429) {
        setAuditError(t.errors.tooManyRequests);
        resetState();
        recaptchaRef.current?.reset();
        return;
      }

      const data = await response.json();

      if (data === "Local Business Not Found") {
        setAuditError(t.errors.notFound);
        resetState();
      } else if (data === "Error" || data?.err) {
        setAuditError(
          data?.err === true
            ? t.errors.invalidEmail
            : t.errors.analysis,
        );
        resetState();
      } else {
        const resp = buildAuditCriteria(data, lang);
        setGMBInfo(resp);
        setFullAccess(!!isSA);
        setDisplayGMBSection(true);
        resetState();
      }
      recaptchaRef.current?.reset();
    } catch (e) {
      if (e?.message?.includes("Too many") || e?.message?.includes("429")) {
        setAuditError(t.errors.tooManyRequests);
      } else {
        setAuditError(t.errors.generic);
      }
      resetState();
      recaptchaRef.current?.reset();
    }
  };

  return (
    <div>
      <Seo
        routeId="gbpAudit"
        title={t.seo.title}
        description={t.seo.description}
      />
      <Hero
        title={t.hero.title}
        subtitle={t.hero.subtitle}
        ctaText={t.hero.cta}
        ctaOnClick={() => document.querySelector('.search-form')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
      />
      <SearchBar
        handleOpenModalBusinessInfo={handleOpenModalBusinessInfo}
        isLoading={isLoading}
        setBusinessAddress={setBusinessAddress}
        businessAddress={businessAddress}
        submitBusiness={submitBusiness}
        isSa={isSA}
        texts={t.search}
      />

      {isLoading && (
        <div className="audit-loader">
          <div className="audit-loader__bar">
            <div className="audit-loader__bar-fill" />
          </div>
          <p className="audit-loader__text">{loadingMessage}</p>
          <div className="audit-loader__steps">
            {LOADING_DELAYS.map((_, i) => (
              <span
                key={i}
                className={`audit-loader__dot${i <= loadingStep ? " audit-loader__dot--active" : ""}`}
              />
            ))}
          </div>
        </div>
      )}

      {auditError && !isLoading && (
        <div className="audit-error">
          <div className="audit-error__icon">!</div>
          <p className="audit-error__text">{auditError}</p>
          <button
            className="audit-error__retry"
            onClick={() => setAuditError(null)}
          >
            {t.errors.retry}
          </button>
        </div>
      )}

      <ReCAPTCHA
        sitekey="6LcLC9UZAAAAAOY_vgaU__i3Qhs1tmHtvbibjD2V"
        size="invisible"
        // French page: browser language, as before. English page: English badge.
        hl={lang === "en" ? "en" : undefined}
        ref={recaptchaRef}
      />
      {!displayGMBSection && !auditError && !isLoading && <InfoStats />}
      <GMBScoring
        hasFullAccess={hasFullAccess}
        displayGMBSection={displayGMBSection}
        googleInfo={GMBInfo}
      />
      <ModalMoreInfo
        modalBusinessInfo={modalBusinessInfo}
        setModalBusinessInfo={setModalBusinessInfo}
        submitBusiness={submitBusiness}
        businessAddress={businessAddress}
        title={t.modal.title}
        submitLabel={t.modal.submit}
      />
      <section className="seo-content">
        <div className="seo-content__inner">
          {t.seoSection(demoUrl(lang, "audit-google-my-business"))}
        </div>
      </section>
      <PageFooter />
    </div>
  );
};

export default Home;
